import Swal from "sweetalert2";
import ObservacaoPedido from "../types/ObservacaoPedido";
import Pagamento from "../types/Pagamento";
import { api } from "./useApi";

export const PedidosApi = () => ({
  getCamposPersonalizados: async () => {
    try {
      const response = await api.get(
        "/usuario/configuracoes_campos_personalizados",
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  setCamposPersonalizados: async (campos: {}) => {
    try {
      const response = await api.post(
        "/usuario/atualizar_campos_personalizados_pedidos",
        { campos: campos },
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  getPedidos: async (
    campos: string[],
    datas: { inicio: string; fim: string },
    filtro: {
      usuario: string;
      catalogo: string;
      pesquisa: string;
      erp_empresa: string | boolean;
      cupom: string;
    },
  ) => {
    try {
      const response = await api.post("pedidos/listar_todos", {
        campos: campos,
        datas: datas,
        filtro: filtro,
      });
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  getSituacoes: async () => {
    try {
      const response = await api.get("pedido/situacoes");
      return response.data as { id: number | string; descricao: string }[];
    } catch (error) {
      console.log(error);
      return [{ id: 0, descricao: "" }];
    }
  },

  alterarSituacao: async (
    ids_pedidos: number[] | string[],
    situacao_id: number,
  ) => {
    const response = await api
      .post("pedidos/altera_situacao_em_lote", {
        ids_pedidos: ids_pedidos,
        situacao_id: situacao_id,
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: "Erro ao alterar pedido(s).",
          showConfirmButton: false,
          timer: 1000,
        });
        throw error.response;
      });
    return response.data;
  },

  getPedido: async (id_pedido: string) => {
    try {
      const response = await api.post(`pedido/listar`, {
        ids_pedidos: [id_pedido],
      });
      return response.data[0];
    } catch (error) {
      console.log(error);
    }
  },

  getPedidosImpressao: async (ids_pedidos: string[]) => {
    try {
      const [pedidos, informacoesPagamento] = await Promise.all([
        api.post(`pedido/listar`, { ids_pedidos: ids_pedidos }),
        api.post(`pedido/select_pagamento`, { ids_pedidos: ids_pedidos }),
      ]);

      const observacoesPorPedido = await Promise.all(
        pedidos.data.map((pedido: any) =>
          api.post(`pedido/observacoes/listar`, { id_pedido: pedido.id_pedido })
            .then(res => ({ id_pedido: pedido.id_pedido, observacoes: res.data }))
            .catch(() => ({ id_pedido: pedido.id_pedido, observacoes: [] }))
        )
      );

      const pedidosImpressao = pedidos.data.map((pedido: any) => {
        const pagamento = informacoesPagamento.data.find(
          (pagamento: any) => pagamento.id_pedido === pedido.id_pedido,
        );
        const obsData = observacoesPorPedido.find(
          (obs: any) => obs.id_pedido === pedido.id_pedido,
        );
        return {
          ...pedido,
          informacoesPagamento: { ...pagamento },
          observacoesPedido: obsData?.observacoes || [],
        };
      });
      return pedidosImpressao;
    } catch (error) {
      console.log(error);
    }
  },

  getInformacoesPagamento: async (id_pedido: string) => {
    try {
      const response = await api.post(`pedido/select_pagamento`, {
        ids_pedidos: [id_pedido],
      });
      return response.data[0] as Pagamento;
    } catch (error) {
      console.log(error);
      return undefined;
    }
  },

  atualizarObservacaoInternaPedido: async (
    id_pedido: string,
    observacao: string,
  ) => {
    try {
      const response = await api.post(`pedido/inserir_observacao_interna`, {
        id_pedido: id_pedido,
        observacao_interna: observacao,
      });
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  forcarSincronizacaoPedido: async (id_pedido: string | number) => {
    const response = await api.post(`pedido/forcar_sincronizacao`, {
      id_pedido: id_pedido,
    });
    return response.data;
  },

  atualizarDadosCliente: async (
    id_pedido: string | number,
    dados: Record<string, any>,
  ) => {
    const response = await api.post(`pedido/atualizar_dados_cliente`, {
      id_pedido,
      ...dados,
    });
    return response.data;
  },

  atualizarCodigoDeRastreio: async (
    id_pedido: string | number,
    codigo_rastreio: string,
  ) => {
    if(!id_pedido) return;
    const response = await api.post(`pedido/inserir_codigo_rastreio`, {
      id_pedido: id_pedido,
      codigo_rastreio: codigo_rastreio,
    });
    return response.data;
  },

  exportarPedidos: async (ids_pedidos: string[], campos?: string[]) => {
    const response = await api.post("pedidos/exportar", {
      ids_pedidos: ids_pedidos,
      campos: campos,
    });

    // a api devolve o conteúdo do csv (já com BOM) e o download é montado aqui
    const { arquivo, conteudo } = response.data;

    const blob = new Blob([conteudo], { type: "text/csv;charset=UTF-8" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = arquivo;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  getObservacoesPedido: async (id_pedido: string) => {
    try {
      const response = await api.post(`pedido/observacoes/listar`, { id_pedido });
      return response.data as ObservacaoPedido[];
    } catch (error) {
      console.log(error);
      return [];
    }
  },

  adicionarObservacaoPedido: async (
    id_pedido: string,
    observacao: { texto: string; visivel_cliente: boolean }
  ) => {
    const response = await api.post(`pedido/observacoes/adicionar`, {
      id_pedido,
      ...observacao,
    });
    return response.data as ObservacaoPedido;
  },

  atualizarObservacaoPedido: async (
    id_pedido: string,
    id_observacao: number,
    observacao: { texto: string; visivel_cliente: boolean }
  ) => {
    const response = await api.post(`pedido/observacoes/atualizar`, {
      id_pedido,
      id_observacao,
      ...observacao,
    });
    return response.data as ObservacaoPedido;
  },

  removerObservacaoPedido: async (id_pedido: string, id_observacao: number) => {
    const response = await api.post(`pedido/observacoes/remover`, {
      id_pedido,
      id_observacao,
    });
    return response.data;
  },
});
